import React from "react";

import { useInvalidate } from "@refinedev/core";
import dayjs from "dayjs";
import { Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import {
  CancelledCandidate,
  DeleteResult,
  defaultSelection,
} from "@/pages/events/features/delete-cancelled/cancelled-events";
import { useCancelledEvents } from "@/pages/events/features/delete-cancelled/use-cancelled-events";

type Phase =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready" }
  | { kind: "deleting"; done: number; total: number }
  | { kind: "done"; result: DeleteResult };

const GROUPS: {
  title: string;
  match: (c: CancelledCandidate) => boolean;
}[] = [
  {
    title: "Marked cancelled",
    match: (c) => c.mark === "sure" && !c.keptReason,
  },
  {
    title: "Title mentions cancelled — tick the ones to delete",
    match: (c) => c.mark === "maybe" && !c.keptReason,
  },
  {
    title: "Kept — sells tickets, so it can have orders. Delete by hand.",
    match: (c) => !!c.keptReason,
  },
];

export function DeleteCancelledModal() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [phase, setPhase] = React.useState<Phase>({ kind: "loading" });
  const [candidates, setCandidates] = React.useState<CancelledCandidate[]>([]);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const { find, remove } = useCancelledEvents();
  const invalidate = useInvalidate();

  const load = async () => {
    setPhase({ kind: "loading" });
    try {
      const found = await find();
      setCandidates(found);
      setSelected(defaultSelection(found));
      setPhase({ kind: "ready" });
    } catch (error) {
      setPhase({ kind: "error", message: toHumanErrorMessage(error) });
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (phase.kind === "deleting") return;
    setIsOpen(open);
    if (open) load();
  };

  const toggle = (id: string, checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });

  const handleDelete = async () => {
    const ids = [...selected];
    setPhase({ kind: "deleting", done: 0, total: ids.length });
    const result = await remove(ids, (done) =>
      setPhase({ kind: "deleting", done, total: ids.length }),
    );
    setPhase({ kind: "done", result });
    // Typesense learns about a delete through the Firestore trigger, a few
    // seconds later; refetching at once would still list the deleted rows.
    setTimeout(
      () => invalidate({ resource: "events", invalidates: ["list"] }),
      4000,
    );
  };

  const nameOf = (id: string) =>
    candidates.find((c) => c.id === id)?.name ?? id;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Trash2 />
          Delete Cancelled
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Delete cancelled events</DialogTitle>
          <DialogDescription>
            Deletes every ticked event. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {phase.kind === "loading" && (
          <div className="flex items-center gap-2 py-6">
            <Spinner /> Looking for cancelled events…
          </div>
        )}

        {phase.kind === "error" && (
          <p className="py-6 text-destructive">
            Search failed: {phase.message}
          </p>
        )}

        {(phase.kind === "ready" || phase.kind === "deleting") && (
          <ScrollArea className="h-[55vh] pr-3">
            {candidates.length === 0 && <p>No cancelled events found.</p>}
            {GROUPS.map((group) => {
              const rows = candidates.filter(group.match);
              if (rows.length === 0) return null;
              return (
                <section key={group.title} className="mb-4">
                  <h3 className="mb-1 text-sm font-semibold">
                    {group.title} ({rows.length})
                  </h3>
                  {rows.map((c) => (
                    <CandidateRow
                      key={c.id}
                      candidate={c}
                      checked={selected.has(c.id)}
                      disabled={!!c.keptReason || phase.kind !== "ready"}
                      onCheckedChange={(v) => toggle(c.id, v)}
                    />
                  ))}
                </section>
              );
            })}
          </ScrollArea>
        )}

        {phase.kind === "deleting" && (
          <div className="flex flex-col gap-1">
            <Progress value={(phase.done / phase.total) * 100} />
            <span className="text-sm">
              Deleting {phase.done} / {phase.total}… keep this window open.
            </span>
          </div>
        )}

        {phase.kind === "done" && (
          <div className="flex flex-col gap-2 py-4">
            <p>
              Deleted {phase.result.deleted.length}. Failed{" "}
              {phase.result.failed.length}.
            </p>
            {phase.result.failed.map((f) => (
              <p key={f.id} className="text-sm text-destructive">
                {nameOf(f.id)} — {f.message}
              </p>
            ))}
          </div>
        )}

        <DialogFooter className="flex gap-2">
          {phase.kind === "done" || phase.kind === "error" ? (
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                disabled={phase.kind === "deleting"}
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={phase.kind !== "ready" || selected.size === 0}
                onClick={handleDelete}
              >
                Delete {selected.size}{" "}
                {selected.size === 1 ? "event" : "events"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CandidateRow({
  candidate,
  checked,
  disabled,
  onCheckedChange,
}: {
  candidate: CancelledCandidate;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  const where = [candidate.city, candidate.venue].filter(Boolean).join(" · ");
  return (
    <div className="flex items-center gap-2 py-1 text-sm">
      <Checkbox
        aria-label={candidate.name}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v) => onCheckedChange(v === true)}
      />
      <a
        href={`/events/edit/${candidate.id}`}
        target="_blank"
        rel="noreferrer"
        className="flex-1 truncate hover:underline"
      >
        {candidate.name}
      </a>
      {candidate.keptReason && <Badge variant="outline">has ticketing</Badge>}
      <span className="w-24 shrink-0 text-muted-foreground">
        {candidate.startDate
          ? dayjs.unix(candidate.startDate).format("DD/MM/YYYY")
          : "no date"}
      </span>
      <span className="w-48 shrink-0 truncate text-muted-foreground">
        {where}
      </span>
    </div>
  );
}
